"use client";

import {
  type FileMetadata,
  FormCheckbox,
  FormFileUpload,
  FormInputField,
  FormMultiSelect,
  FormRichTextEditor,
  FormSelectField,
  FormTextarea,
  ScrollArea,
} from "@atlas/core";
import { FilterBuilder, useTranslation } from "@atlas/sdk";
import { IHubCatalogStatus } from "@enums";
import type { IHubCatalog, IHubCatalogType } from "@models";
import { useSearchHubCatalogs } from "@query";
import { safeDecodeBase64Json } from "@shared";
import { useEffect, useMemo } from "react";
import { type Control, type UseFormSetValue, useWatch } from "react-hook-form";
import { z } from "zod";

export interface ICatalogDetailsFormValues {
  typeId: string;
  label: string;
  description: string;
  token?: string;
  overview: string;
  supportedCloud: string[];
  documentationLocations: string[];
  parentCatalogIds: string[];
  tierId: string;
  addon: boolean;
  featured: boolean;
  icon: FileMetadata;
}

type CatalogDetailsSelectOption = { value: string; label: string };

interface ICatalogDetailsFormProps {
  isLoading: boolean;
  control: Control<ICatalogDetailsFormValues>;
  setValue: UseFormSetValue<ICatalogDetailsFormValues>;
  catalogTypes: IHubCatalogType[];
  tierOptions: CatalogDetailsSelectOption[];
  cloudOptions: CatalogDetailsSelectOption[];
  excludeCatalogId?: number;
}

export function CatalogDetailsForm({
  isLoading,
  control,
  setValue,
  catalogTypes,
  tierOptions,
  cloudOptions,
  excludeCatalogId,
}: ICatalogDetailsFormProps) {
  const { t } = useTranslation();

  const { data: catalogsData } = useSearchHubCatalogs({
    filter: new FilterBuilder()
      .in("status", [IHubCatalogStatus.Approved, IHubCatalogStatus.Submitted])
      .build(),
    mode: "DATA",
  });
  const catalogs = useMemo(
    () => catalogsData?.pages?.flatMap((p) => p.items) ?? [],
    [catalogsData],
  );

  const typeId = useWatch({ control, name: "typeId", defaultValue: "" });
  const labelValue = useWatch({ control, name: "label", defaultValue: "" });
  const descriptionValue = useWatch({
    control,
    name: "description",
    defaultValue: "",
  });

  const catalogTypeOptions = useMemo(
    () =>
      catalogTypes.map((ct) => {
        const isAddon =
          Array.isArray(ct.parentCatalogTypes) &&
          ct.parentCatalogTypes.length > 0;
        const baseLabel = ct.label || ct.name || String(ct.typeId ?? "");
        return {
          value: String(ct.typeId ?? 0),
          label: `${baseLabel}`,
          description: `${isAddon ? "Addon" : "Catalog"}`,
        };
      }),
    [catalogTypes],
  );

  const selectedCatalogType = useMemo(() => {
    const numericTypeId = typeId ? parseInt(typeId, 10) : 0;
    return catalogTypes.find((ct) => (ct.typeId ?? 0) === numericTypeId);
  }, [catalogTypes, typeId]);

  const hasParentCatalogTypes =
    Array.isArray(selectedCatalogType?.parentCatalogTypes) &&
    selectedCatalogType.parentCatalogTypes.length > 0;

  const parentCatalogOptions = useMemo(() => {
    const catalogList = catalogs;
    const typeList = catalogTypes;
    const hasNoParentCatalogs = (c: IHubCatalog) =>
      c.parentCatalogIds == null || c.parentCatalogIds.length === 0;

    const allowedTypeIds =
      selectedCatalogType?.parentCatalogTypes.map(Number) || [];

    // Use flatMap instead of map to return a single, un-grouped array of options
    return typeList
      .filter((ct) => allowedTypeIds.includes(+(ct.typeId ?? 0)))
      .flatMap((ct) => {
        const catalogsOfType = catalogList.filter(
          (c) =>
            c.typeId != null &&
            c.typeId === (ct.typeId ?? -1) &&
            (c.status === IHubCatalogStatus.Approved ||
              c.status === IHubCatalogStatus.Submitted) &&
            hasNoParentCatalogs(c) &&
            !(
              excludeCatalogId != null &&
              excludeCatalogId > 0 &&
              c.catalogId === excludeCatalogId
            ),
        );

        const uniqueCatalogs = Array.from(
          new Map(catalogsOfType.map((c) => [c.catalogId, c])).values(),
        );

        if (uniqueCatalogs.length === 0) return [];

        const groupLabel = ct.label || ct.name || String(ct.typeId ?? "");

        // Map directly to flat options, moving the group context into the description
        return uniqueCatalogs.map((c) => ({
          value: String(c.catalogId ?? 0),
          label: c.label || c.name || String(c.catalogId ?? ""),
          // Combine the group label and item description so the user still has context
          description: [groupLabel, c.description].filter(Boolean).join(" — "),
        }));
      });
  }, [catalogs, catalogTypes, excludeCatalogId, selectedCatalogType]);

  useEffect(() => {
    if (hasParentCatalogTypes) {
      setValue("addon", true, { shouldValidate: true });
    } else {
      setValue("addon", false, { shouldValidate: true });
    }
  }, [hasParentCatalogTypes, setValue]);

  return (
    <ScrollArea className="min-h-0 flex-1">
      <div className="mx-auto grid max-w-2xl gap-4 md:grid-cols-2 sm:grid-cols-1 md:items-start ">
        <FormSelectField
          control={control}
          name="typeId"
          label={t("hub.catalog_details.catalog_type")}
          items={catalogTypeOptions}
          placeholder={t("hub.create_catalog.catalog_type_placeholder")}
          description={t("hub.create_catalog.catalog_type_description")}
          enableSearch
          isLoading={isLoading}
          required
        />

        <FormInputField
          isLoading={isLoading}
          control={control}
          name="label"
          label={t("hub.create_catalog.name")}
          placeholder={t("hub.create_catalog.name_placeholder")}
          description={`${t("hub.create_catalog.name_description")} (${labelValue.length}/50)`}
          maxLength={50}
          required
        />

        <FormSelectField
          isLoading={isLoading}
          control={control}
          name="tierId"
          label={t("hub.catalog_details.config_tier")}
          items={tierOptions}
          placeholder={t("hub.create_catalog.tier_placeholder")}
          description={t("hub.create_catalog.tier_description")}
          required
        />

        {hasParentCatalogTypes && (
          <FormMultiSelect
            control={control}
            name="parentCatalogIds"
            label={t("hub.create_catalog.parent_catalogs")}
            items={parentCatalogOptions}
            placeholder={t("hub.create_catalog.parent_catalogs_placeholder")}
            description={t("hub.create_catalog.parent_catalogs_description")}
            isLoading={isLoading}
          />
        )}

        <FormInputField
          isLoading={isLoading}
          control={control}
          name="token"
          label={t("hub.catalog_details.config_token")}
          placeholder={t("hub.create_catalog.token_placeholder")}
          description={t("hub.create_catalog.token_description")}
          type="password"
          validate={(val: unknown) => {
            const value = val as string | undefined;
            if (!value) return true;
            const res = z
              .string()
              .base64(
                t("hub.create_catalog.token_invalid", {
                  defaultValue: "Invalid token",
                }),
              )
              .refine(
                (base64Val) => {
                  try {
                    const parsed =
                      safeDecodeBase64Json<Record<string, unknown>>(base64Val);
                    // Check if it's an object and contains companyId
                    return (
                      parsed &&
                      typeof parsed === "object" &&
                      "companyId" in parsed
                    );
                  } catch {
                    return false;
                  }
                },
                {
                  message: t("hub.create_catalog.token_invalid", {
                    defaultValue: "Invalid token",
                  }),
                },
              )
              .safeParse(value);
            return (
              res.success ||
              res.error.issues[0]?.message ||
              "Invalid token format"
            );
          }}
        />

        <FormMultiSelect
          isLoading={isLoading}
          control={control}
          name="supportedCloud"
          label={t("hub.catalog_details.config_supported_cloud", {
            defaultValue: "Supported Cloud",
          })}
          items={cloudOptions}
          placeholder={t("hub.create_catalog.supported_cloud_tooltip", {
            defaultValue:
              "Cloud providers where this catalog item can be deployed.",
          })}
          description={t("hub.create_catalog.supported_cloud_description", {
            defaultValue:
              "Cloud providers where this catalog item can be deployed.",
          })}
          required
        />

        <FormFileUpload
          control={control}
          variant="input"
          name="icon"
          label={t("hub.create_catalog.icon", {
            defaultValue: "Icon",
          })}
          placeholder={t("hub.create_catalog.icon_placeholder", {
            defaultValue: "Upload catalog icon (.ico, .png only)",
          })}
          description={t("hub.create_catalog.icon_description", {
            defaultValue: "Upload a catalog icon. Allowed formats: .ico, .png.",
          })}
          maxFiles={1}
          fileType={[".png", ".ico"]}
          isLoading={isLoading}
        />

        {hasParentCatalogTypes && (
          <FormCheckbox
            control={control}
            name="addon"
            variant="box"
            disabled
            label={t("hub.create_catalog.addon")}
            description={t("hub.create_catalog.addon_description")}
            isLoading={isLoading}
          />
        )}

        <FormCheckbox
          control={control}
          name="featured"
          variant="box"
          label={t("hub.catalog_manager.featured", {
            defaultValue: "Featured",
          })}
          description={t("hub.create_catalog.featured_description", {
            defaultValue:
              "Highlight this catalog in the hub with a featured badge.",
          })}
          isLoading={isLoading}
        />

        <div className="md:col-span-2 min-h-32">
          <FormTextarea
            control={control}
            name="description"
            label={t("hub.catalog_subscribe.solution_description")}
            placeholder={t("hub.create_catalog.description_placeholder")}
            description={`${t("hub.create_catalog.description_description")} (${descriptionValue.length}/250)`}
            isLoading={isLoading}
            maxLength={250}
          />
        </div>
        <div className="md:col-span-2 min-h-32">
          <FormRichTextEditor
            control={control}
            name="overview"
            label={t("hub.create_catalog.overview")}
            placeholder={t("hub.create_catalog.overview_placeholder")}
            description={`${t("hub.create_catalog.overview_description")}`}
            required
            isLoading={isLoading}
            maxHeight={300}
          />
        </div>
      </div>
    </ScrollArea>
  );
}
